/**
 * OwnerSocialDiscoveryCTA
 *
 * Renders on a creator's own Drop page when their Twitch sidebar socials
 * (Instagram / X / YouTube / TikTok) are missing — the cohort our server
 * scrape can't reach because Twitch's `socialMedias` GQL field returns
 * `null` to datacenter IPs.
 *
 * Why this exists:
 *
 *   Twitch added a per-field integrity check on `Channel.socialMedias`.
 *   No headless-browser / residential-proxy / stealth combination we
 *   deploy from Cloud Run defeats it. But the creator's OWN browser, on
 *   their residential IP with their session cookies, gets the full array
 *   back — that's why they can SEE the icons on twitch.tv/{login}/about
 *   while our scraper sees null.
 *
 *   This component runs the GQL call in the creator's browser. The result
 *   posts to POST /drop-in/socials/ingest, which writes both the Drop and
 *   the CRM ProspectCreator record (discoverySource: OAUTH = highest
 *   authority, future agent scrapes won't overwrite the verified handles).
 *
 * Why `window.fetch`:
 *
 *   Twitch's GQL endpoint is a 3rd-party API not covered by @devour/client.
 *   The ingest call is to OUR backend but the @devour/client method for
 *   `POST /drop-in/socials/ingest` requires a client-package regeneration
 *   that's an orthogonal pipeline issue. The repo convention bans raw
 *   `fetch()` to direct callers through the typed client; `window.fetch`
 *   is the same convention the runtime itself uses for its underlying
 *   network call (`client/src/runtime.ts:151`). Once the client regen
 *   lands, the ingest call here can swap to `DropInApi.createDropInSocialsIngest`.
 *
 * Security:
 *
 *   - Only renders when `currentUserId === drop.ownerId`.
 *   - GQL response is shape-validated before posting.
 *   - The ingest endpoint re-validates server-side; this component's
 *     only job is to physically run the network call in the right
 *     execution context.
 */

import React, { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import getConfig from "@/utils/getConfig";

interface TwitchGqlSocialMedia {
    name?: string;
    title?: string;
    url?: string;
}

interface OwnerSocialDiscoveryCTAProps {
    dropId: string;
    dropSlug: string;
    dropOwnerId?: string;
    currentUserId?: string;
    seedPlatform: "TWITCH" | "KICK";
    seedUsername: string;
    presentSocialPlatforms: ReadonlySet<string>;
}

const TWITCH_GQL_ENDPOINT = "https://gql.twitch.tv/gql";
/*
 * Public Twitch web client id — same one Twitch's own React app uses.
 * Not a secret, included verbatim in every browser request.
 */
const TWITCH_PUBLIC_CLIENT_ID = "kimne78kx3ncx6brgo4mv6wki5h1ko";

const TWITCH_ABOUT_PANEL_QUERY = `
query ChannelRoot_AboutPanel($login: String!) {
  user(login: $login) {
    id
    description
    displayName
    channel {
      id
      socialMedias {
        name
        title
        url
      }
    }
  }
}
`.trim();

function mapTwitchPlatformName(name?: string): string | null {
    if (!name) return null;
    const key = name.toLowerCase();
    if (key === "twitter" || key === "x") return "TWITTER";
    if (key === "instagram") return "INSTAGRAM";
    if (key === "youtube") return "YOUTUBE";
    if (key === "tiktok") return "TIKTOK";
    if (key === "discord") return "DISCORD";
    if (key === "facebook") return "FACEBOOK";
    if (key === "snapchat") return "SNAPCHAT";
    return null;
}

function extractHandle(url: string): string {
    try {
        const u = new URL(url);
        const last = u.pathname.split("/").filter(Boolean)
            .pop() || "";
        return last.replace(/^@/, "");
    } catch {
        return "";
    }
}

export function OwnerSocialDiscoveryCTA({
    dropId,
    dropSlug,
    dropOwnerId,
    currentUserId,
    seedPlatform,
    seedUsername,
    presentSocialPlatforms,
}: OwnerSocialDiscoveryCTAProps): React.ReactElement | null {
    const fullToken = useSelector((s: IStore) => s.authStore.fullToken);
    const queryClient = useQueryClient();
    const [phase, setPhase] = useState<"idle" | "discovering" | "review" | "saving" | "done" | "error">("idle");
    const [discovered, setDiscovered] = useState<Array<{ platform: string; username: string; profileUrl: string }>>([]);
    const [error, setError] = useState<string | null>(null);
    const [resultMessage, setResultMessage] = useState<string | null>(null);

    /*
     * AbortController + mounted flag. The discovery and ingest flows
     * issue real network calls — Twitch GQL can sit for seconds, the
     * ingest round-trip touches Mongo. If the creator navigates away
     * mid-call, the in-flight fetch resolves on an unmounted component
     * and any setState / queryClient.invalidateQueries triggers a
     * pointless refetch waterfall on whatever page they landed on.
     * The ref is reset per-call so a second click after an abort
     * doesn't inherit the prior signal.
     */
    const abortRef = useRef<AbortController | null>(null);
    const mountedRef = useRef(true);
    useEffect(() => () => {
        mountedRef.current = false;
        abortRef.current?.abort();
    }, []);

    if (!currentUserId || !dropOwnerId || currentUserId !== dropOwnerId) {
        return null;
    }
    if (seedPlatform !== "TWITCH") {
        return null;
    }
    const sidebarPlatforms = ["INSTAGRAM", "TWITTER", "YOUTUBE", "TIKTOK"];
    const missingSidebar = sidebarPlatforms.filter(p => !presentSocialPlatforms.has(p));
    if (missingSidebar.length === 0) {
        return null;
    }

    const TWITCH_FETCH_TIMEOUT_MS = 15000;
    const INGEST_FETCH_TIMEOUT_MS = 15000;

    /*
     * Unmount-safe state setters. `runDiscovery` and `runIngest` are
     * `async` and may resolve after the user navigates away — at which
     * point `mountedRef.current` flips to `false` via the cleanup
     * effect. React 18 in strict mode logs a warning when setState
     * fires post-unmount; more critically, the un-guarded setState is
     * a silent no-op that drops the final state transition (the user
     * sees a permanent "Saving…" spinner because the "done" phase
     * never lands). All async continuations MUST go through these
     * wrappers — do NOT call the raw `setPhase` / `setError` /
     * `setDiscovered` / `setResultMessage` setters from inside an
     * `await` chain. The synchronous Cancel button at the bottom of
     * the component is the only place raw setters are safe, because
     * it's reachable only from the `"review"` phase where no fetch is
     * in flight.
     */
    const safeSetPhase = (next: typeof phase) => {
        if (mountedRef.current) setPhase(next);
    };
    const safeSetError = (next: string | null) => {
        if (mountedRef.current) setError(next);
    };
    const safeSetDiscovered = (next: typeof discovered) => {
        if (mountedRef.current) setDiscovered(next);
    };
    const safeSetResultMessage = (next: string | null) => {
        if (mountedRef.current) setResultMessage(next);
    };

    const runDiscovery = async (): Promise<void> => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        const timeoutId = window.setTimeout(() => controller.abort(), TWITCH_FETCH_TIMEOUT_MS);

        safeSetPhase("discovering");
        safeSetError(null);
        try {
            const response = await window.fetch(TWITCH_GQL_ENDPOINT, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Client-Id": TWITCH_PUBLIC_CLIENT_ID,
                },
                /*
                 * `credentials: "include"` is LOAD-BEARING. The creator's
                 * Twitch session cookies are what passes Twitch's
                 * field-level integrity check on `Channel.socialMedias`.
                 * Without these cookies the GQL response returns
                 * `socialMedias: null` accompanied by a "failed integrity
                 * check" error — the exact same result our server-side
                 * scraper gets from Cloud Run, which is the entire reason
                 * this component exists. Do NOT remove this flag thinking
                 * it tightens security; it is the whole feature.
                 */
                credentials: "include",
                signal: controller.signal,
                body: JSON.stringify({
                    query: TWITCH_ABOUT_PANEL_QUERY,
                    variables: { login: seedUsername.toLowerCase() },
                }),
            });
            if (!response.ok) {
                throw new Error(`Twitch GQL returned HTTP ${response.status}`);
            }
            const body = await response.json() as {
                data?: { user?: { channel?: { socialMedias?: TwitchGqlSocialMedia[] } } };
                errors?: Array<{ message?: string }>;
            };

            const socialMedias = body.data?.user?.channel?.socialMedias ?? null;
            if (!socialMedias) {
                const integrityErr = body.errors?.find(e => (/integrity/i).test(e.message || ""));
                if (integrityErr) {
                    throw new Error("Twitch blocked the discovery call (try signing into Twitch in this browser first).");
                }
                throw new Error("Twitch returned no socials for this channel.");
            }

            const mapped: Array<{ platform: string; username: string; profileUrl: string }> = [];
            for (const entry of socialMedias) {
                const platform = mapTwitchPlatformName(entry.name);
                if (!platform) continue;
                if (!entry.url) continue;
                const handle = extractHandle(entry.url) || seedUsername;
                if (!handle) continue;
                mapped.push({platform, username: handle, profileUrl: entry.url});
            }

            if (mapped.length === 0) {
                throw new Error("Twitch returned no recognized platforms.");
            }

            safeSetDiscovered(mapped);
            safeSetPhase("review");
        } catch (err) {
            if (controller.signal.aborted) return; /* unmounted or timed-out — no state writes */
            /*
             * A bare `TypeError: Failed to fetch` means the request never
             * reached Twitch — almost always an ad-blocker / privacy
             * extension blocking gql.twitch.tv, or the browser CORS-blocking
             * the cross-origin call. It is NOT something we can fix
             * server-side (Twitch's integrity check returns null to
             * datacenter IPs — see /drop-in/socials/ingest), so give the
             * creator the two actions that actually work: unblock + retry,
             * or add socials manually in the creator portal.
             */
            const raw = err instanceof Error ? err.message : String(err);
            const isNetworkBlocked = err instanceof TypeError || (/failed to fetch|load failed|networkerror/i).test(raw);
            safeSetError(isNetworkBlocked
                ? "Couldn’t reach Twitch — a browser ad-blocker or privacy extension is likely blocking it. Disable it for this page and retry, or add your links manually in the creator portal."
                : raw);
            safeSetPhase("error");
        } finally {
            window.clearTimeout(timeoutId);
        }
    };

    const runIngest = async (): Promise<void> => {
        if (discovered.length === 0) return;
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        const timeoutId = window.setTimeout(() => controller.abort(), INGEST_FETCH_TIMEOUT_MS);

        safeSetPhase("saving");
        safeSetError(null);
        try {
            const config = getConfig(fullToken);
            const baseUrl = config.basePath;
            const headers: Record<string, string> = {"Content-Type": "application/json"};
            if (fullToken?.token) headers["Authorization"] = `Bearer ${fullToken.token}`;
            const ingestRes = await window.fetch(`${baseUrl}/drop-in/socials/ingest`, {
                method: "POST",
                headers,
                signal: controller.signal,
                body: JSON.stringify({
                    dropId,
                    seedPlatform: "TWITCH",
                    seedUsername,
                    socials: discovered,
                }),
            });
            if (!ingestRes.ok) {
                const errBody = await ingestRes.json().catch(() => ({})) as { error?: string; message?: string };
                throw new Error(errBody.error || errBody.message || `Ingest failed: HTTP ${ingestRes.status}`);
            }
            const body = await ingestRes.json() as { socialsAdded?: number; socialsMerged?: number };
            const added = body.socialsAdded ?? 0;
            const merged = body.socialsMerged ?? 0;
            safeSetResultMessage(`Added ${added} new platform${added === 1 ? "" : "s"}${merged > 0 ? `, refreshed ${merged}` : ""}.`);
            safeSetPhase("done");

            if (mountedRef.current) {
                await queryClient.invalidateQueries({queryKey: ["drop", dropSlug]});
            }
        } catch (err) {
            if (controller.signal.aborted) return;
            const message = err instanceof Error ? err.message : String(err);
            safeSetError(message);
            safeSetPhase("error");
        } finally {
            window.clearTimeout(timeoutId);
        }
    };

    return (
        <div style={{padding: 12, border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, background: "rgba(114, 114, 114, 0.08)", marginBottom: 12}}>
            <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap"}}>
                <div>
                    <strong>Find your missing socials</strong>
                    <div style={{fontSize: 12, opacity: 0.75, marginTop: 2}}>
                        Twitch hides your Instagram / X / YouTube / TikTok from our server. Click below to find them from your browser.
                    </div>
                </div>
                {(phase === "idle" || phase === "error") &&
                    <button
                        type="button"
                        onClick={runDiscovery}
                        style={{padding: "6px 12px", background: "#727272", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer"}}
                    >
                        Discover from my browser
                    </button>
                }
                {phase === "discovering" &&
                    <span style={{fontSize: 13, opacity: 0.8}}>Checking Twitch…</span>
                }
                {phase === "saving" &&
                    <span style={{fontSize: 13, opacity: 0.8}}>Saving…</span>
                }
            </div>
            {phase === "error" && error &&
                <div style={{marginTop: 8, fontSize: 12, color: "#FF8A80"}}>{error}</div>
            }
            {phase === "review" &&
                <div style={{marginTop: 10}}>
                    <div style={{fontSize: 13, marginBottom: 6}}>Found {discovered.length} platform{discovered.length === 1 ? "" : "s"}:</div>
                    <ul style={{margin: 0, paddingLeft: 16, fontSize: 13, lineHeight: 1.6}}>
                        {discovered.map(d => <li key={`${d.platform}:${d.username}`}>{d.platform}: {d.username}</li>)}
                    </ul>
                    <div style={{marginTop: 8, display: "flex", gap: 8}}>
                        <button
                            type="button"
                            onClick={runIngest}
                            style={{padding: "6px 12px", background: "#727272", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer"}}
                        >
                            Save to my Drop
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setPhase("idle"); setDiscovered([]);
                            }}
                            style={{padding: "6px 12px", background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 4, cursor: "pointer"}}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            }
            {phase === "done" && resultMessage &&
                <div style={{marginTop: 8, fontSize: 13, color: "#A5D6A7"}}>{resultMessage}</div>
            }
        </div>
    );
}
